import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'

// Type definitions for recommendation log entries
export interface RecommendationLogRefinement {
  similarToGifId?: string
  excludeSimilarToGifIds?: string[]
  feedback?: 'similar' | 'different'
}

export interface RecommendationLogEntry {
  id: string
  requestId: string
  timestamp: string
  input: {
    conversationSnippet?: string
    userIntent?: string
    preferences?: Record<string, unknown>
    refinement?: RecommendationLogRefinement
  }
  processing: {
    modelVersions: {
      queryGeneration: string
      ranking: string
    }
    candidatesFound?: number
    searchCandidateIds?: string[]

    // Stage-A retrieval candidate pool (gifId + cosine score) in retrieval-rank
    // order. Optional/backward-compatible: older log docs won't have it.
    retrievalPool?: Array<{
      gifId: string
      score: number
    }>

    // Unified analysis structure
    unifiedAnalysis: {
      totalDocumentsEvaluated: number
      finalRecommendationCount: number
    }
  }
  results: {
    recommendationCount: number
    recommendations: Array<{
      gifId: string
      rank: number
      reasoning: string
      strengths: string[]
      limitations?: string[]
    }>
  }
  performance: {
    timingBreakdown: {
      documentFetchMs: number
      aiProcessingMs: number
    }
    tokenUsage?: {
      processing?: {
        model: string
        input_tokens?: number
        output_tokens?: number
        prompt_tokens?: number
        completion_tokens?: number
        total_tokens?: number
        estimated_cost_usd: {
          input: string
          output: string
          total: string
        }
      }
    }
    processingTimeMs: number
  }
  success: boolean
  error?: string
}

// Type definitions for cloud function responses
interface GetRecommendationLogsResponse {
  success: boolean
  data?: {
    logs: RecommendationLogEntry[]
    totalCount: number
    hasMore: boolean
  }
  error?: string
}

// Create callable function instance
const getRecommendationLogsFunction = httpsCallable<
  {
    limit?: number
    offset?: number
    startDate?: string
    endDate?: string
    success?: boolean
    isTestRun?: boolean
    testRunId?: string
  },
  GetRecommendationLogsResponse
>(functions, 'getRecommendationLogs')

export const RecommendationLogsService = {
  /**
   * Fetch recommendation logs with optional filtering and pagination
   */
  async getLogs(
    options: {
      limit?: number
      offset?: number
      startDate?: Date
      endDate?: Date
      success?: boolean
      isTestRun?: boolean
      testRunId?: string
    } = {}
  ): Promise<{
    success: boolean
    data?: {
      logs: RecommendationLogEntry[]
      totalCount: number
      hasMore: boolean
    }
    error?: string
  }> {
    try {
      // Convert Date objects to ISO strings for transmission
      const params: {
        limit?: number
        offset?: number
        startDate?: string
        endDate?: string
        success?: boolean
        isTestRun?: boolean
        testRunId?: string
      } = {}

      if (options.limit !== undefined) params.limit = options.limit
      if (options.offset !== undefined) params.offset = options.offset
      if (options.startDate) params.startDate = options.startDate.toISOString()
      if (options.endDate) params.endDate = options.endDate.toISOString()
      if (options.success !== undefined) params.success = options.success
      if (options.isTestRun !== undefined) params.isTestRun = options.isTestRun
      if (options.testRunId) params.testRunId = options.testRunId

      const result = await getRecommendationLogsFunction(params)
      return result.data
    } catch (error) {
      console.error('Error calling getRecommendationLogs function:', error)
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch recommendation logs',
      }
    }
  },

  /**
   * Format processing time for display
   */
  formatProcessingTime(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`
    } else {
      return `${(ms / 60000).toFixed(1)}m`
    }
  },

  /**
   * Format cost for display
   */
  formatCost(costUsd: string): string {
    const cost = parseFloat(costUsd)
    if (cost < 0.01) {
      return `<$0.01`
    }
    return `$${cost.toFixed(4)}`
  },

  /**
   * Get status color for success/failure display
   */
  getStatusColor(success: boolean): 'success' | 'danger' {
    return success ? 'success' : 'danger'
  },

  /**
   * Get status text for success/failure display
   */
  getStatusText(success: boolean): string {
    return success ? 'Success' : 'Failed'
  },
}
